type FaceApi = typeof import('face-api.js');

export interface FaceCrop {
  position: string;
  size: string;
}

const cache = new Map<string, FaceCrop>();
const inflight = new Map<string, Promise<FaceCrop>>();

export const DEFAULT_FACE_CROP: FaceCrop = { position: '50% 22%', size: '160%' };

let apiPromise: Promise<FaceApi> | null = null;
function ensureApi(): Promise<FaceApi> {
  if (!apiPromise) {
    apiPromise = import('face-api.js')
      .then(async (mod) => {
        await mod.nets.tinyFaceDetector.loadFromUri('/models');
        return mod;
      })
      .catch((err) => {
        apiPromise = null;
        throw err;
      });
  }
  return apiPromise;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('face-crop: image load failed'));
    img.src = src;
  });
}

function cropToCss(
  cx: number,
  cy: number,
  side: number,
  imgW: number,
  imgH: number,
): FaceCrop {
  // Clamp square so it stays inside the image; may reduce side.
  const maxSide = Math.min(imgW, imgH);
  const s = Math.min(side, maxSide);
  const x = Math.max(0, Math.min(imgW - s, cx - s / 2));
  const y = Math.max(0, Math.min(imgH - s, cy - s / 2));
  const sizeW = (imgW / s) * 100;
  const sizeH = (imgH / s) * 100;
  const posX = imgW === s ? 50 : (x / (imgW - s)) * 100;
  const posY = imgH === s ? 50 : (y / (imgH - s)) * 100;
  return {
    position: `${posX.toFixed(1)}% ${posY.toFixed(1)}%`,
    size: `${sizeW.toFixed(1)}% ${sizeH.toFixed(1)}%`,
  };
}

export async function getFaceCrop(dataUrl: string): Promise<FaceCrop> {
  const cached = cache.get(dataUrl);
  if (cached) return cached;
  const existing = inflight.get(dataUrl);
  if (existing) return existing;

  const promise = (async (): Promise<FaceCrop> => {
    try {
      const faceapi = await ensureApi();
      const img = await loadImage(dataUrl);
      const detection = await faceapi.detectSingleFace(
        img,
        new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }),
      );
      if (!detection) return DEFAULT_FACE_CROP;
      const { x, y, width, height } = detection.box;
      // Expand the face box to a head crop (extra room for hair + chin).
      const padX = width * 0.35;
      const padTop = height * 0.55;
      const padBottom = height * 0.25;
      const hx = x - padX;
      const hy = y - padTop;
      const hw = width + padX * 2;
      const hh = height + padTop + padBottom;
      const cx = hx + hw / 2;
      const cy = hy + hh / 2;
      const side = Math.max(hw, hh);
      return cropToCss(cx, cy, side, img.naturalWidth, img.naturalHeight);
    } catch {
      return DEFAULT_FACE_CROP;
    }
  })();

  inflight.set(dataUrl, promise);
  const result = await promise;
  cache.set(dataUrl, result);
  inflight.delete(dataUrl);
  return result;
}
